<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router'
import { useDisplay, useTheme } from 'vuetify'
import { VIconBtn } from 'vuetify/labs/VIconBtn'
import { useStaffStore } from '../stores/staffStore'
import type { IActivityFilterChip } from '../types/IActivityFilterChip'
import type { IEmployeeActivityItem } from '../types/IEmployeeActivityItem'
import type { IStaffEmployee } from '../types/IStaffEmployee'
import EditEmployeeDialog from './EditEmployeeDialog.vue'
import EmployeeProfileSidebar from '@/components/createdUi/EmployeeProfileSidebar.vue'
import ReportTemplateMenu from '@/components/createdUi/ReportTemplateMenu.vue'
import { usePermissionsStore } from '@/modules/permissions/stores/permissionsStore'
import { useTableItemsPerPage } from '@/composables/useTableItemsPerPage'
import ActivityByDaysWidget from '@/modules/dashboard/components/ActivityByDaysWidget.vue'
import ActivityByModulesWidget from '@/modules/dashboard/components/ActivityByModulesWidget.vue'
import MultipleCountWidget from '@/modules/dashboard/components/MultipleCountWidget.vue'
import type { IActivityByDaysWidget } from '@/modules/dashboard/types/IActivityByDaysWidget'
import type { IActivityByModulesWidget } from '@/modules/dashboard/types/IActivityByModulesWidget'
import type { IMultipleCountWidget } from '@/modules/dashboard/types/IMultipleCountWidget'
import type { IWidget } from '@/modules/dashboard/types/IWidget'
import { formatDateTime, formatRelativeTime } from '@/utils/formatters'
import { getRandomAvatar } from '@/utils/getRandomAvatar'
import { useOnboardingContext } from '@/modules/onboarding/composables/useOnboardingContext'
import { STAFF_ANCHORS, STAFF_CONTEXTS } from '@/modules/onboarding/scenarios/staff/anchors'
import { ERouteNames } from '@/router/ERouteNames'

const props = defineProps<Props>()
const { smAndDown } = useDisplay()
const theme = useTheme()
const isEditModalOpen = ref(false)

interface Props {
  id?: string
}

const route = useRoute()
const router = useRouter()
const staffStore = useStaffStore()
const permissionsStore = usePermissionsStore()

// Онбординг открывает страницу на учебном сотруднике из таблицы и по «Назад» возвращается туда же
useOnboardingContext({
  names: [STAFF_CONTEXTS.activity],
  root: () => document.body,
  actions: {
    backToStaff: async () => { await router.push({ name: ERouteNames.STAFF }) },
  },
})

// Active ID
const activeEmployeeId = computed(() => {
  return props.id || (route.params.id as string) || (staffStore.staffList[0]?.id)
})

// Active Employee
const employee = ref<IStaffEmployee | null>(null)

const timePeriod = ref('last_31_days')
const timePeriods = [
  { title: 'Сегодня', value: 'today' },
  { title: 'Вчера', value: 'yesterday' },
  { title: 'Текущая неделя', value: 'current_week' },
  { title: 'Предыдущая неделя', value: 'last_week' },
  { title: 'Текущий месяц', value: 'current_month' },
  { title: 'Предыдущий месяц', value: 'last_month' },
  { title: 'Последние 31 день', value: 'last_31_days' },
  { title: 'Текущий год', value: 'current_year' },
  { title: 'Предыдущий год', value: 'last_year' },
  { title: 'За все время', value: 'all_time' },
]

// Состояния раскрытия кастомных панелей (аккордеонов)
const isAnalyticsExpanded = ref(true)
const isChartExpanded = ref(true)
const isActionsExpanded = ref(true)

const isProfileSidebarOpen = ref(false)

function openProfile() {
  isProfileSidebarOpen.value = true
}

const isLoadingActivity = ref(false)

// Виджеты на основе реактивных ref-состояний
const nsWidgetData = ref<IMultipleCountWidget>({
  id: 3,
  name: 'НС',
  description: '',
  icon: 'ri-alert-line',
  show_icon: false,
  order: 3,
  widget_type: 'multiple_count',
  data: [],
})

const ticketsWidgetData = ref<IMultipleCountWidget>({
  id: 4,
  name: 'Заявки',
  description: '',
  icon: 'ri-file-list-3-line',
  show_icon: false,
  order: 4,
  widget_type: 'multiple_count',
  data: [],
})

const daysWidgetData = ref<IActivityByDaysWidget>({
  id: 1,
  name: 'По дням',
  description: 'Действий за выбранный период',
  icon: 'ri-calendar-line',
  show_icon: false,
  order: 1,
  widget_type: 'activity_by_days',
  data: {
    period: 'За всё время',
    value: 0,
    trend: 0,
    labels: [],
    datasets: [],
  },
})

const modulesWidgetData = ref<IActivityByModulesWidget>({
  id: 2,
  name: 'По модулям',
  description: 'Распределение активности по модулям',
  icon: 'ri-pie-chart-line',
  show_icon: false,
  order: 2,
  widget_type: 'activity_by_modules',
  data: {
    labels: [],
    datasets: [],
  },
})

const getAvatar = computed(() => {
  if (employee.value?.avatar) {
    return employee.value.avatar
  }
  return getRandomAvatar()
})

const lastLoginTime = computed(() => {
  return employee.value?.last_login ? formatRelativeTime(employee.value.last_login) : 'никогда'
})

const serverActions = ref<IEmployeeActivityItem[]>([])
const nextCursor = ref<string | null>(null)
const prevCursor = ref<string | null>(null)
const totalActionsCount = ref(0)
const { itemsPerPage } = useTableItemsPerPage('employeeActivity', 20)
const currentPage = ref(1)

const activityHeaders = computed(() => [
  { title: 'РАЗДЕЛ', key: 'module', sortable: false, width: '200px' },
  { title: 'ДАТА', key: 'date', sortable: false, width: '180px' },
  { title: 'ДЕЙСТВИЕ', key: 'action', sortable: false },
])

const hideNextButton = computed(() => {
  const isFetchReady = nextCursor.value === null
  const isMaxPage = Math.ceil(totalActionsCount.value / itemsPerPage.value) <= currentPage.value
  return isFetchReady || isMaxPage
})

async function handlePageChange(options: any) {
  const page = Number(options.page || 1)
  const perPage = Number(options.itemsPerPage || 20)

  currentPage.value = page

  if (perPage !== itemsPerPage.value) {
    itemsPerPage.value = perPage
    currentPage.value = 1
    serverActions.value = []
    nextCursor.value = null
    prevCursor.value = null
    await loadEmployeeActivity()
    return
  }

  const itemsNeeded = page * perPage
  const currentLoaded = serverActions.value.length

  if (isLoadingActivity.value) return

  if (itemsNeeded > currentLoaded && nextCursor.value) {
    isLoadingActivity.value = true
    try {
      let cursorParam: string | null = null
      try {
        cursorParam = new URL(nextCursor.value).searchParams.get('cursor')
      } catch (e) {
        cursorParam = nextCursor.value
      }

      const res = await staffStore.fetchLatestActions(activeEmployeeId.value, cursorParam || undefined, perPage)
      if (res) {
        serverActions.value = [...serverActions.value, ...(res.results || [])]
        nextCursor.value = res.next || null
        prevCursor.value = res.previous || null
        totalActionsCount.value = res.count || 0
      }
    } catch (e) {
      console.error('Ошибка догрузки действий:', e)
    } finally {
      isLoadingActivity.value = false
    }
  }
}

async function loadEmployeeActivity() {
  if (!activeEmployeeId.value) return
  isLoadingActivity.value = true
  try {
    const [latestActionsRes, widgets] = await Promise.all([
      staffStore.fetchLatestActions(activeEmployeeId.value, undefined, itemsPerPage.value).catch(e => {
        console.error('Ошибка fetchLatestActions:', e)
        return null
      }),
      staffStore.fetchEmployeeActivity(activeEmployeeId.value, timePeriod.value).catch(e => {
        console.error('Ошибка fetchEmployeeActivity:', e)
        return []
      }),
    ])

    if (latestActionsRes) {
      serverActions.value = latestActionsRes.results || []
      nextCursor.value = latestActionsRes.next || null
      prevCursor.value = latestActionsRes.previous || null
      totalActionsCount.value = latestActionsRes.count || 0
      currentPage.value = 1
    }

    if (widgets?.length) {
      applyActivityWidgets(widgets)
    }
  } catch (err) {
    console.error('Ошибка загрузки активности сотрудника:', err)
  } finally {
    isLoadingActivity.value = false
  }
}

function applyActivityWidgets(widgets: IWidget[]) {
  const ns = widgets.find((w: IWidget) => w.name === 'Сводная аналитика НС') as IMultipleCountWidget | undefined
  if (ns) {
    nsWidgetData.value.description = ns.description || ''
    nsWidgetData.value.data = ns.data || []
  }

  const tickets = widgets.find((w: IWidget) => w.name === 'Сводная аналитика заявок') as IMultipleCountWidget | undefined
  if (tickets) {
    ticketsWidgetData.value.description = tickets.description || ''
    ticketsWidgetData.value.data = tickets.data || []
  }

  const days = widgets.find((w: IWidget) => w.name === 'Активность по дням') as any
  if (days) {
    const period = days.data?.period || 'За всё время'
    const labels = days.data?.labels || []
    const datasets = days.data?.datasets || []

    let totalValue = 0
    if (datasets.length > 0 && datasets[0].data) {
      totalValue = datasets[0].data.reduce((acc: number, v: number) => acc + v, 0)
    }

    daysWidgetData.value.name = days.name || 'По дням'
    daysWidgetData.value.description = days.description || 'Действий за выбранный период'
    daysWidgetData.value.data = {
      period,
      value: totalValue,
      trend: 0,
      labels: labels.map((l: any) => ({
        id: typeof l === 'object' ? String(l.id || l.name) : String(l),
        name: typeof l === 'object' ? String(l.name) : String(l),
      })),
      datasets: datasets.map((d: any) => ({
        label: d.label || 'действий',
        data: d.data || [],
        active_days: d.active_days || [],
      })),
    }
  }

  const modules = widgets.find((w: IWidget) => w.name === 'Активность по модулям') as any
  if (modules) {
    const labels = modules.data?.labels || []
    const datasets = modules.data?.datasets || []

    const defaultColors = [
      'rgb(var(--v-theme-error))',
      'rgb(var(--v-theme-primary))',
      'rgb(var(--v-theme-success))',
      'rgb(var(--v-theme-warning))',
      'rgb(var(--v-theme-primary-darken-1))',
      'rgb(var(--v-theme-info))',
      'rgb(var(--v-theme-secondary))',
    ]

    modulesWidgetData.value.name = modules.name || 'По модулям'
    modulesWidgetData.value.description = modules.description || 'Распределение активности по модулям'
    modulesWidgetData.value.data = {
      labels: labels.map((l: any) => ({
        id: typeof l === 'object' ? String(l.id || l.name) : String(l),
        name: typeof l === 'object' ? String(l.name) : String(l),
      })),
      datasets: datasets.map((d: any) => ({
        label: d.label || 'Активность',
        data: d.data || [],
        colors: d.colors || defaultColors.slice(0, d.data?.length || 0),
      })),
    }
  }
}

async function loadEmployeeDetails() {
  if (!activeEmployeeId.value) {
    employee.value = null
    return
  }

  const remoteEmp = await staffStore.fetchEmployeeById(activeEmployeeId.value)
  employee.value = remoteEmp
}

watch(() => activeEmployeeId.value, async newId => {
  if (newId) {
    await Promise.all([
      loadEmployeeDetails(),
      loadEmployeeActivity(),
    ])
  }
}, { immediate: true })

watch(timePeriod, async () => {
  if (!activeEmployeeId.value) return
  isLoadingActivity.value = true
  try {
    const widgets = await staffStore.fetchEmployeeActivity(activeEmployeeId.value, timePeriod.value).catch(e => {
      console.error('Ошибка fetchEmployeeActivity:', e)
      return []
    })
    if (widgets?.length) {
      applyActivityWidgets(widgets)
    }
  } finally {
    isLoadingActivity.value = false
  }
})

// Отслеживаем закрытие диалога редактирования, чтобы обновить профиль при изменениях
watch(isEditModalOpen, async isOpen => {
  if (!isOpen) {
    await loadEmployeeDetails()
  }
})

// Фильтрация последних действий
const selectedModuleFilter = ref('Все')
const dateAfterFilter = ref('')
const dateBeforeFilter = ref('')

const getFilterIcon = (isActive: boolean) => isActive ? 'ri-filter-fill' : 'ri-filter-2-line'

const isModuleFilterMenuOpen = ref(false)
const isDateFilterMenuOpen = ref(false)

function applyDateFilter() {
  isDateFilterMenuOpen.value = false
}

const parseDateFromInput = (dateString: string): Date | null => {
  if (!dateString) {
    return null
  }
  const [day, month, year] = dateString.split('.').map(Number)
  if (!day || !month || !year) {
    return null
  }
  return new Date(year, month - 1, day)
}

// Активные чипы фильтров
const activeFilterChips = computed<IActivityFilterChip[]>(() => {
  const chips: IActivityFilterChip[] = []

  if (selectedModuleFilter.value !== 'Все') {
    chips.push({
      label: `Раздел: ${selectedModuleFilter.value}`,
      type: 'module',
    })
  }

  if (dateAfterFilter.value || dateBeforeFilter.value) {
    let dateLabel = 'Дата: '
    if (dateAfterFilter.value && dateBeforeFilter.value) {
      dateLabel += `с ${dateAfterFilter.value} по ${dateBeforeFilter.value}`
    } else if (dateAfterFilter.value) {
      dateLabel += `с ${dateAfterFilter.value}`
    } else if (dateBeforeFilter.value) {
      dateLabel += `по ${dateBeforeFilter.value}`
    }
    chips.push({
      label: dateLabel,
      type: 'date',
    })
  }
  return chips
})

// Удаление фильтра по типу
const removeFilter = (type: IActivityFilterChip['type']) => {
  if (type === 'module') {
    selectedModuleFilter.value = 'Все'
  } else if (type === 'date') {
    dateAfterFilter.value = ''
    dateBeforeFilter.value = ''
  }
}

// Сброс всех фильтров
const resetAllFilters = () => {
  selectedModuleFilter.value = 'Все'
  dateAfterFilter.value = ''
  dateBeforeFilter.value = ''
}

function formatAction(item: IEmployeeActivityItem) {
  let prefix = item.action_display || ''
  if (prefix === '1') {
    prefix = 'Создание'
  }
  return `${prefix}: ${item.object_display}`
}

function getModuleFromAction(item: IEmployeeActivityItem) {
  return item.source || 'Прочее'
}

// Список последних действий сотрудника с сервера
const activities = computed(() => {
  return serverActions.value.map((item, index) => {
    const dateObj = item.created_at ? new Date(item.created_at) : new Date()
    const dateStr = formatDateTime(item.created_at)

    return {
      id: item.id || index,
      module: getModuleFromAction(item),
      date: dateStr,
      action: formatAction(item),
      rawDate: new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate()),
    }
  })
})
// Отфильтрованные действия
const filteredActivities = computed(() => {
  let list = activities.value

  if (selectedModuleFilter.value !== 'Все') {
    list = list.filter(item => item.module === selectedModuleFilter.value)
  }
  if (dateAfterFilter.value) {
    const afterDate = parseDateFromInput(dateAfterFilter.value)
    if (afterDate) {
      list = list.filter(item => item.rawDate >= afterDate)
    }
  }
  if (dateBeforeFilter.value) {
    const beforeDate = parseDateFromInput(dateBeforeFilter.value)
    if (beforeDate) {
      list = list.filter(item => item.rawDate <= beforeDate)
    }
  }

  return list
})

const displayedActivities = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return filteredActivities.value.slice(start, end)
})
</script>

<template>
  <div
    class="d-flex flex-column pa-0 pa-sm-6"
    :style="smAndDown ? 'background-color: rgb(var(--v-theme-surface)) !important;' : 'background-color: var(--v-theme-background) !important;'"
  >
    <!-- Единый контейнер (как в макете) -->
    <div
      style="overflow: hidden;"
      :style="smAndDown ? 'background-color: rgb(var(--v-theme-surface)) !important; border: none !important; border-radius: 0 !important; box-shadow: none !important;' : 'background-color: rgb(var(--v-theme-surface)) !important; border: 1px solid rgba(var(--v-theme-on-surface), 0.12) !important; border-radius: 8px !important; box-shadow: 0 2px 6px rgba(0, 0, 0, 0.01) !important;'"
    >
      <!-- Раздел профиля сотрудника -->
      <div
        class="d-flex justify-space-between border-b pa-4 pa-sm-6"
        :class="smAndDown ? 'flex-column align-stretch ga-4' : 'align-center ga-4'"
        :data-onboarding="STAFF_ANCHORS.activity.profile"
      >
        <div v-if="employee" :class="smAndDown ? 'd-flex flex-column align-center text-center ga-3 w-full' : 'd-flex align-center ga-3'">
          <VAvatar
            :image="getAvatar"
            variant="tonal"
            size="80"
            class="flex-shrink-0"
          />
          <div class="d-flex flex-column">
            <span
              class="profile-name"
              style="font-family: 'Inter', sans-serif !important; font-size: 15px !important; font-weight: 500 !important; color: rgb(var(--v-theme-primary)) !important; line-height: 20px !important; cursor: pointer;"
              @click="openProfile"
            >
              {{ employee.full_name || `${employee.last_name} ${employee.first_name} ${employee.middle_name}` }}
            </span>
            <span style="font-family: 'Inter', sans-serif !important; font-size: 13px !important; font-weight: 400 !important; color: rgba(var(--v-theme-on-surface), 0.55) !important; line-height: 20px !important;">{{ employee.position?.name || 'Должность не указана' }}</span>
            <span style="font-family: 'Inter', sans-serif !important; font-size: 13px !important; font-weight: 400 !important; color: rgba(var(--v-theme-on-surface), 0.55) !important; line-height: 20px !important;">{{ employee.department?.name || 'Подразделение не указано' }}</span>
            <span style="font-family: 'Inter', sans-serif !important; font-size: 13px !important; font-weight: 400 !important; color: rgba(var(--v-theme-on-surface), 0.55) !important; line-height: 20px !important;">Рабочий телефон: {{ employee.work_phone_number || 'не указан' }}</span>
          </div>
        </div>

        <div :class="smAndDown ? 'd-flex flex-column align-stretch ga-2 w-full' : 'd-flex align-center ga-2'">
          <div :style="smAndDown ? 'width: 100% !important;' : 'width: 260px !important;'">
            <VSelect
              v-model="timePeriod"
              :items="timePeriods"
              item-title="title"
              item-value="value"
              variant="outlined"
              density="compact"
              hide-details
              class="period-select"
            />
          </div>
          <ReportTemplateMenu
            v-if="employee?.id === activeEmployeeId && permissionsStore.modules.employee_management?.employee_view"
            object-type="employee"
            :object-id="activeEmployeeId"
            variant="toolbar"
          />
          <VBtn
            v-if="employee?.editable_fields?.length"
            :data-onboarding="STAFF_ANCHORS.activity.edit"
            variant="outlined"
            color="primary"
            prepend-icon="ri-edit-line"
            height="40"
            class="edit-profile-btn text-none"
            @click="isEditModalOpen = true"
          >
            Редактировать
          </VBtn>
        </div>
      </div>

      <!-- Панель 1: Сводная аналитика -->
      <div style="border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12) !important;" :data-onboarding="STAFF_ANCHORS.activity.analytics">
        <div
          class="card-header d-flex align-center justify-space-between pa-3"
          style="cursor: pointer; user-select: none; background: transparent;"
          @click="isAnalyticsExpanded = !isAnalyticsExpanded"
        >
          <span style="font-family: 'Inter', sans-serif !important; font-size: 15px !important; font-weight: 500 !important; color: rgba(var(--v-theme-on-surface), 0.9) !important;">Сводная аналитика</span>
          <div class="d-flex align-center justify-center rounded-circle" style="width: 38px; height: 38px;">
            <VIcon :icon="isAnalyticsExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'" size="20" class="text-medium-emphasis" />
          </div>
        </div>
        <VExpandTransition>
          <div v-show="isAnalyticsExpanded" style="overflow: hidden;">
            <div class="pa-4 pa-sm-6 border-t">
              <VRow class="align-center">
                <!-- НС -->
                <VCol cols="12" md="4" class="d-flex flex-column border-right-divider">
                  <div class="text-subtitle-1 font-weight-medium mb-3 text-high-emphasis">
                    НС
                  </div>
                  <MultipleCountWidget :widget-data="nsWidgetData" :loading="isLoadingActivity" flat />
                </VCol>

                <!-- Заявки -->
                <VCol cols="12" md="4" class="d-flex flex-column border-right-divider">
                  <div class="text-subtitle-1 font-weight-medium mb-3 text-high-emphasis">
                    Заявки
                  </div>
                  <MultipleCountWidget :widget-data="ticketsWidgetData" :loading="isLoadingActivity" flat />
                </VCol>

                <!-- Последний вход -->
                <VCol cols="12" md="4" class="d-flex flex-column">
                  <div class="text-subtitle-1 font-weight-medium mb-3 text-high-emphasis">
                    Последний вход
                  </div>
                  <div class="d-flex align-center gap-3">
                    <VAvatar
                      color="secondary"
                      variant="tonal"
                      rounded
                      size="40"
                    >
                      <VIcon icon="ri-login-box-line" size="22" />
                    </VAvatar>
                    <div class="d-flex flex-column">
                      <span class="text-body-1 font-weight-semibold text-high-emphasis">{{ lastLoginTime }}</span>
                    </div>
                  </div>
                </VCol>
              </VRow>
            </div>
          </div>
        </VExpandTransition>
      </div>

      <!-- Панель 2: Активность по дням и модулям -->
      <div style="border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12) !important;">
        <div
          class="card-header d-flex align-center justify-space-between pa-3"
          style="cursor: pointer; user-select: none; background: transparent;"
          @click="isChartExpanded = !isChartExpanded"
        >
          <span style="font-family: 'Inter', sans-serif !important; font-size: 15px !important; font-weight: 500 !important; color: rgba(var(--v-theme-on-surface), 0.9) !important;">Активность по дням и модулям</span>
          <div class="d-flex align-center justify-center rounded-circle" style="width: 38px; height: 38px;">
            <VIcon :icon="isChartExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'" size="20" class="text-medium-emphasis" />
          </div>
        </div>
        <VExpandTransition>
          <div v-show="isChartExpanded" style="overflow: visible;">
            <div class="pa-4 pa-sm-6 border-t">
              <VRow>
                <!-- Слева: По дням -->
                <VCol cols="12" md="8" class="border-right-divider">
                  <ActivityByDaysWidget
                    v-if="daysWidgetData.data.datasets.length > 0 && daysWidgetData.data.datasets[0].data?.length > 0"
                    :widget-data="daysWidgetData"
                    :loading="isLoadingActivity"
                    flat
                  />
                  <div
                    v-else-if="!isLoadingActivity"
                    class="d-flex align-center justify-center text-grey-darken-1 h-100 min-h-120 text-body-2"
                  >
                    Нет данных по активности за период
                  </div>
                </VCol>

                <!-- Справа: По модулям -->
                <VCol cols="12" md="4">
                  <ActivityByModulesWidget
                    v-if="modulesWidgetData.data.datasets.length > 0 && modulesWidgetData.data.datasets[0].data?.length > 0"
                    :widget-data="modulesWidgetData"
                    :loading="isLoadingActivity"
                    flat
                  />
                  <div
                    v-else-if="!isLoadingActivity"
                    class="d-flex align-center justify-center text-grey-darken-1 h-100 min-h-120 text-body-2"
                  >
                    Нет данных по распределению активности
                  </div>
                </VCol>
              </VRow>
            </div>
          </div>
        </VExpandTransition>
      </div>

      <!-- Панель 3: Последние действия -->
      <div style="border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12) !important; border-bottom: none !important;" :data-onboarding="STAFF_ANCHORS.activity.actions">
        <div
          class="card-header d-flex align-center justify-space-between pa-3"
          style="cursor: pointer; user-select: none; background: transparent;"
          @click="isActionsExpanded = !isActionsExpanded"
        >
          <span style="font-family: 'Inter', sans-serif !important; font-size: 15px !important; font-weight: 500 !important; color: rgba(var(--v-theme-on-surface), 0.9) !important;">Последние действия</span>
          <div class="d-flex align-center justify-center rounded-circle" style="width: 38px; height: 38px;">
            <VIcon :icon="isActionsExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'" size="20" class="text-medium-emphasis" />
          </div>
        </div>
        <VExpandTransition>
          <div v-show="isActionsExpanded" style="overflow: hidden;">
            <div class="pa-0 border-t">
              <!-- Активные фильтры -->
              <div
                v-if="activeFilterChips.length"
                class="filter-chips-bar d-flex align-center flex-wrap ga-2 pa-3 pa-sm-4"
              >
                <VBtn
                  variant="outlined"
                  color="secondary"
                  size="small"
                  prepend-icon="ri-filter-off-line"
                  class="text-color-secondary text-none"
                  @click="resetAllFilters"
                >
                  Сбросить фильтры
                </VBtn>
                <VChip
                  v-for="chip in activeFilterChips"
                  :key="chip.type"
                  closable
                  size="small"
                  class="text-body-2"
                  @click:close="removeFilter(chip.type)"
                >
                  {{ chip.label }}
                </VChip>
              </div>

              <VDataTableServer
                :items="displayedActivities"
                :items-length="totalActionsCount"
                :headers="activityHeaders"
                :items-per-page="itemsPerPage"
                :loading="isLoadingActivity"
                class="no-row-gaps custom-activity-table"
                :class="[{ 'dark-theme': theme.global.current.value.dark }]"
                @update:options="handlePageChange"
              >
                <!-- Кастомный заголовок модуля -->
                <template #[`header.module`]>
                  <div class="d-flex align-center gap-1">
                    <span>РАЗДЕЛ</span>
                    <VMenu v-model="isModuleFilterMenuOpen" close-on-content-click location="bottom start">
                      <template #activator="slotProps">
                        <VIconBtn
                          v-bind="slotProps.props"
                          variant="plain"
                          size="small"
                          class="filter-icon-btn"
                          :icon="getFilterIcon(selectedModuleFilter !== 'Все')"
                        />
                      </template>
                      <VList density="compact">
                        <VListItem
                          v-for="mod in ['Все', 'Заявки', 'НС', 'Журналы', 'Аудиты', 'Новости', 'ППУ', 'Задачи', 'Прочее']"
                          :key="mod"
                          @click="selectedModuleFilter = mod"
                        >
                          <VListItemTitle>{{ mod }}</VListItemTitle>
                        </VListItem>
                      </VList>
                    </VMenu>
                  </div>
                </template>

                <!-- Кастомный заголовок даты -->
                <template #[`header.date`]>
                  <div class="d-flex align-center gap-1">
                    <span>ДАТА</span>
                    <VMenu
                      v-model="isDateFilterMenuOpen"
                      :close-on-content-click="false"
                      location="bottom start"
                      transition="scale-transition"
                      :max-width="220"
                    >
                      <template #activator="slotProps">
                        <VIconBtn
                          v-bind="slotProps.props"
                          variant="plain"
                          size="small"
                          class="filter-icon-btn"
                          :icon="getFilterIcon(!!(dateAfterFilter || dateBeforeFilter))"
                        />
                      </template>
                      <DateRangeFilterCard
                        :date-after="dateAfterFilter"
                        :date-before="dateBeforeFilter"
                        @update:date-after="dateAfterFilter = $event"
                        @update:date-before="dateBeforeFilter = $event"
                        @apply="applyDateFilter"
                      />
                    </VMenu>
                  </div>
                </template>

                <template #no-data>
                  <div class="text-center text-medium-emphasis pa-4">
                    Данные отсутствуют
                  </div>
                </template>

                <!-- Отображение ячеек -->
                <template #[`item.module`]="{ item }">
                  <span class="td-module">{{ item.module }}</span>
                </template>
                <template #[`item.date`]="{ item }">
                  <span class="td-date">{{ item.date }}</span>
                </template>
                <template #[`item.action`]="{ item }">
                  <span class="td-action">{{ item.action }}</span>
                </template>

                <template #bottom>
                  <div
                    class="custom-data-table-footer"
                    :class="{ 'hide-next-button': hideNextButton }"
                  >
                    <VDataTableFooter
                      show-current-page
                      :items-per-page-options="[20, 50, 100]"
                    />
                  </div>
                </template>
              </VDataTableServer>
            </div>
          </div>
        </VExpandTransition>
      </div>
    </div>

    <EmployeeProfileSidebar
      v-model="isProfileSidebarOpen"
      :employee-id="activeEmployeeId"
    />

    <EditEmployeeDialog
      v-model="isEditModalOpen"
      :employee-id="activeEmployeeId"
    />
  </div>
</template>

<style scoped lang="scss">
.profile-name {
  &:hover {
    text-decoration: underline;
  }
}

.period-select {
  :deep(.v-field__control) {
    border-radius: 6px !important;
    height: 48px !important;
  }

  :deep(.v-field__input) {
    font-family: 'Inter', sans-serif !important;
    font-size: 15px !important;
    font-weight: 400 !important;
    color: rgba(var(--v-theme-on-surface), 0.9) !important;
    padding-inline: 16px !important;
  }
}

.card-header {
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(var(--v-theme-on-surface), 0.01) !important;
  }
}

.min-h-120 {
  min-height: 120px;
}

// Стилизация таблицы последних действий
.custom-activity-table {
  background: transparent !important;
  width: 100% !important;
  border-collapse: collapse;

  :deep(.v-table__wrapper table) {
    min-width: 700px !important;
  }

  :deep(.v-table__wrapper table thead tr th) {
    --v-overlay-scrim-opacity: 1 !important;

    background: rgb(var(--v-theme-primary-100), var(--v-overlay-scrim-opacity)) !important;
    height: 56px !important;
    font-family: 'Inter', sans-serif !important;
    font-size: 13px !important;
    font-weight: 500 !important;
    color: rgba(var(--v-theme-on-surface), 0.9) !important;
    text-align: left !important;
    padding-inline: 20px 16px !important;
    border-bottom: none !important;
    position: relative;

    &:not(:last-child)::after {
      content: '';
      position: absolute;
      right: 0;
      top: 25%;
      height: 50%;
      width: 1px;
      background-color: rgba(var(--v-theme-on-surface), 0.12);
    }

    @media (max-width: 600px) {
      padding-inline: 12px 8px !important;
    }

    &.th-module {
      width: 200px !important;

      @media (max-width: 600px) {
        width: 100px !important;
        min-width: 100px !important;
      }
    }

    &.th-date {
      width: 230px !important;

      @media (max-width: 600px) {
        width: 110px !important;
        min-width: 110px !important;
      }
    }
  }

  :deep(thead tr) {
    height: 46px !important;

    &:hover {
      background: transparent !important;
    }
  }

  :deep(tbody) {
    tr {
      height: 46px !important;

      &:hover {
        background: transparent !important; // Отключаем ховер на строках как в макете
      }
    }

    td {
      height: 46px !important;
      font-family: 'Inter', sans-serif !important;
      font-size: 15px !important;
      font-weight: 400 !important;
      color: rgba(var(--v-theme-on-surface), 0.9) !important;
      padding-inline: 20px 16px !important;
      border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12) !important;

      @media (max-width: 600px) {
        padding-inline: 12px 8px !important;
      }

      &.td-module {
        font-weight: 400 !important;
      }

      &.td-date {
        font-weight: 500 !important;
      }

      &.td-action {
        font-weight: 400 !important;
      }
    }
  }
}

// Кастомные стили столбцов графика
.bar-container {
  max-width: 45px;

  .bar-fill {
    width: 16px;
    background-color: rgb(var(--v-theme-primary)) !important;
    opacity: 0.85;

    &:hover {
      opacity: 1;
      transform: scaleX(1.1);
    }
  }
}

.transition-all {
  transition: all 0.2s ease-in-out;
}

.border-right-divider {
  @media (min-width: 960px) {
    border-right: 1px solid rgba(var(--v-theme-on-surface), 0.12) !important;
  }

  @media (max-width: 959px) {
    border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12) !important;
    padding-bottom: 16px !important;
  }
}

.filter-icon-btn {
  color: rgba(var(--v-theme-on-surface), 0.7) !important;
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
}

.filter-chips-bar {
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12) !important;
  background-color: rgba(var(--v-theme-on-surface), 0.01) !important;
}

:deep(.v-data-table-footer) {
  display: flex;
  justify-content: center;
  align-items: center;
}

.custom-data-table-footer {
  :deep(.v-data-table-footer) {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;

    .v-data-table-footer__items-per-page > span:first-child {
      display: none !important;
    }

    .v-data-table-footer__pagination {
      order: 1;
    }

    .v-data-table-footer__items-per-page {
      order: 2;
    }

    .v-data-table-footer__items-per-page .v-select__selection-text {
      overflow: visible;
      text-overflow: clip;
    }

    .v-data-table-footer__info {
      order: 3;
    }

    .v-pagination__last > button {
      display: none !important;
    }

    .v-pagination__next > button[aria-disabled='true'] {
      display: none !important;
    }
  }
}
</style>
